/**
 * Efficient cloning utilities to replace JSON.parse(JSON.stringify()) patterns
 * that cause webpack cache performance issues
 */

/**
 * Efficient deep clone using structuredClone (modern browsers)
 * Falls back to JSON method only if structuredClone is not available
 */
export function deepClone<T>(obj: T): T {
  if (typeof structuredClone !== 'undefined') {
    // Use native structuredClone for better performance
    return structuredClone(obj);
  }
  
  // Fallback for older environments
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Shallow clone for simple objects
 */
export function shallowClone<T extends Record<string, unknown>>(obj: T): T {
  return { ...obj };
}

/**
 * Clone array efficiently
 */
export function cloneArray<T>(arr: T[]): T[] {
  return [...arr];
}

/**
 * Cache key generator that avoids JSON.stringify for simple objects
 */
export function generateCacheKey(obj: unknown): string {
  if (obj === null || obj === undefined) {
    return String(obj);
  }
  
  if (typeof obj === 'object' && obj !== null) {
    // For objects, create key without JSON.stringify to avoid performance issues
    const keys = Object.keys(obj).sort();
    return keys.map(key => {
      const value = (obj as Record<string, unknown>)[key];
      return `${key}:${typeof value === 'object' ? '[object]' : value}`;
    }).join('|');
  }
  
  return String(obj);
} 
